import { computeFieldI18nItems } from '@/common/utilsFilterSchema';
import { attributeFilterSchemaModule } from '@/components/common/filter/filters/AttributeFilterSchemaModule';
import { SimpleFilterOptions } from '@/components/common/filter/filters/types';
import { ObjectsType, ObjectsTypesMap } from '@/store/application/data.assets';
import { translate, translateMessage } from '@/store/languages';
import { IFormLayoutItem, IFormLayoutRow } from '@/uikit/forms/NForm.vue';
import { configModule } from '../config';
import { convertSimilarCyrillicToLatin, firstCase } from '@/common/filters';
import { getLogger } from 'loglevel';
import { commonFilterSchemaModule } from '@/components/common/filter/filters/CommonFilterSchemaModule';

const logger = getLogger('[satisfy]');
logger.setLevel('warn');
export interface FeatureConfig {
  name: string;
  index: number;
  type: FeatureType;
  formatter?: string;
  classes?: string[];
  filter?: {
    name?: string;
    props?: string[];
    excluded?: string[];
    confidence?: string[];
  };
}

type FeatureType = 'class' | 'number' | 'angle' | 'string';

const FeatureTypeMap: Record<string, FeatureType> = {
  Class: 'class',
  Number: 'number',
  Angle: 'angle',
  String: 'string',
};

type FeatureFormatter = 'age' | 'angle';

const FeatureFormatterMap: Record<string, FeatureFormatter> = {
  Age: 'age',
  Angle: 'angle'
};

export interface FeatureValue {
  name: string | number;
  confidence: number;
}

export interface FeaturePropertyResult {
  content: string;
  confidenceContent?: string;
}

export class FeaturesModule {
  constructor() {}

  getFeatureConfigs(type: ObjectsType): FeatureConfig[] {
    const featuresMap: Record<string, FeatureConfig> = configModule.config.objects[type]?.features || {};
    const result: FeatureConfig[] = Object.values(featuresMap).sort((a: FeatureConfig, b: FeatureConfig) => (a.index > b.index ? 1 : -1));
    return result;
  }

  getFeatureProperties(featureProperties: Record<string, FeatureValue>, type: ObjectsType, isCluster: boolean = false): FeaturePropertyResult[] {
    const featureConfigs: FeatureConfig[] = this.getFeatureConfigs(type);
    const results: FeaturePropertyResult[] = [];
    const $t = translate;
    const $tm = translateMessage;

    for (let featureConfig of featureConfigs) {
      const excludedInClusters = isCluster && featureConfig.filter?.excluded?.includes('clusters');
      let property = featureProperties[featureConfig.name];
      let hasProperty = property && property.name && property.confidence > 0.01;
      if (!hasProperty) continue;
      if (excludedInClusters && isCluster) continue;

      let result: FeaturePropertyResult = { content: '' };

      switch (featureConfig.type) {
        case FeatureTypeMap.Number:
          result.content = property.name + '';
          result.confidenceContent = undefined;
          break;
        case FeatureTypeMap.String:
          result.content = property.name + '';
          result.confidenceContent = $t('common.confidence', 'f') + ': ' + property.confidence?.toFixed(2);
          break;
        default:
          result.content =
            $t('features.' + type + '_' + featureConfig.name, 'f') + ': ' + $t('features.' + type + '_' + featureConfig.name + '__' + property.name, 'f');
          result.confidenceContent = $t('common.confidence', 'f') + ': ' + property.confidence?.toFixed(2);
          break;
      }

      if (featureConfig.formatter === 'short') {
        result.content = $t('features.' + type + '_' + featureConfig.name + '__' + property.name, 'f');
      } else if (featureConfig.formatter === 'age') {
        result.content = $tm('common.year_mf', { count: Math.round(+property.name) });
        result.confidenceContent = undefined;
      } else if (featureConfig.formatter === 'angle') {
        result.content = $t('features.' + type + '_' + featureConfig.name, 'f') + ': ' + Math.round(+property.name) + '°';
        result.confidenceContent = undefined;
      } else if (featureConfig.formatter === 'unknown_class') {
        result.content =
          property.name === 'unknown' ? $t(`features.${type}_${featureConfig.name}__unknown`, 'f') : (property.name + '').split('/').map(firstCase).join(' ');
      }

      if (property.name === 'coming_soon') {
        result.content = $t('features.' + type + '_' + featureConfig.name, 'f') + ': ' + $t('features.coming_soon', 'f');
        result.confidenceContent = $t('common.confidence', 'f') + ': ' + property.confidence?.toFixed(2);
      }

      results.push(result);
    }

    return results;
  }

  getFeatureFilters(options: SimpleFilterOptions, type: ObjectsType, isWebhook = false, hasLiveness = true, isCluster = false): IFormLayoutRow[] {
    const featureConfigs: FeatureConfig[] = this.getFeatureConfigs(type);
    let result: IFormLayoutRow[] = [];

    for (let featureConfig of featureConfigs) {
      let item: IFormLayoutItem | IFormLayoutItem[] | null | undefined = null;
      const excludedInClusters = isCluster && featureConfig.filter?.excluded?.includes('clusters');
      const displayConfidence = isWebhook && featureConfig.filter?.confidence?.includes('webhooks');
      
      if (excludedInClusters) continue;
      switch (featureConfig.type) {
        case FeatureTypeMap.Number:
          break;
        case FeatureTypeMap.String:
          const strMergedOptions = {
            small: options.small,
            path: featureConfig.name + '_contains',
            i18n_label: 'features.' + type + '_' + featureConfig.name,
            i18n_tooltip: 'common.enter',
            multiple: true
          };
          item = attributeFilterSchemaModule.getInputFeatureContains(strMergedOptions);
          break;
        default:
          const colorized = featureConfig.name?.indexOf('color') > -1;
          const mergedOptions = {
            small: options.small,
            path: featureConfig.filter?.name || featureConfig.name,
            i18n_label: 'features.' + type + '_' + featureConfig.name,
            items: computeFieldI18nItems(featureConfig.classes || [], 'features.' + type + '_' + featureConfig.name, colorized)
          };
          item = displayConfidence
            ? attributeFilterSchemaModule.getClassFeatureFilterWithConfidence(mergedOptions)
            : attributeFilterSchemaModule.getClassFeatureFilter(mergedOptions);
          break;
      }

      switch (featureConfig.formatter) {
        case FeatureFormatterMap.Age:
          item = attributeFilterSchemaModule.getAgeFeatureFilter({
            small: options.small,
            i18n_label: 'features.' + type + '_' + featureConfig.name
          });
          break;

        case FeatureFormatterMap.Angle:
          item = attributeFilterSchemaModule.getAngleFeatureFilter({
            small: options.small,
            name: featureConfig.name,
            i18n_label: 'features.' + type + '_' + featureConfig.name
          });
          break;
      }

      switch (featureConfig.name) {
        case 'make':
          item = attributeFilterSchemaModule.getCarMake(options);
          break;
        case 'model':
          item = attributeFilterSchemaModule.getCarModel(options);
          break;
        case 'license_plate_number':
          (item as IFormLayoutItem).i18n_tooltip = 'common.enter_number_plate_contains_description';
          (item as IFormLayoutItem).props!.i18n_placeholder = 'common.enter_number_plate';
          break;
        case 'license_plate_country':
          item = attributeFilterSchemaModule.getCarLicensePlateCountry(options);
          break;
      }

      if (item) result.push(item);
    }

    return result;
  }

  satisfyFeaturePropsToFilters(featureProperties: Record<string, FeatureValue>, filters: Record<string, any>, type: ObjectsType): boolean {
    const featureConfigs: FeatureConfig[] = this.getFeatureConfigs(type);
    let result = true;
    let satisfy = false;

    for (let featureConfig of featureConfigs) {
      let featureName = featureConfig.name;
      const property = featureProperties[featureConfig.name];
      const value = property?.name;
      const hasValue = !!(value || !isNaN(value as number));

      switch (featureConfig.type) {
        case FeatureTypeMap.Number:
          if (featureConfig.formatter === FeatureFormatterMap.Angle) {
            featureName += '_angle';
          }
          const filterGte = filters[featureName + '_gte'];
          const filterLte = filters[featureName + '_lte'];
          satisfy = (isNaN(filterGte) ? true : hasValue && value >= filterGte) && (isNaN(filterLte) ? true : hasValue && value <= filterLte);
          result &&= satisfy;
          break;
        case FeatureTypeMap.String:
          const strFilter = filters[featureName + '_contains'];
          satisfy = strFilter ? hasValue && this.findSubstrings(featureName, strFilter, value) : true;
          result &&= satisfy;
          break;
        default:
          const filter = filters[featureConfig.filter?.name || featureName];
          satisfy = Array.isArray(filter) && filter.length > 0 ? hasValue && filter.includes(value) : true;
          result &&= satisfy;
          break;
      }

      if (!result) {
        logger.info('Satisfy result is FALSE for feature: ', featureName, ', value: ', value,', filter: ', filters[featureName], '; All props and filters: ', featureProperties, filters);
        return false;
      }
    }
    logger.info('Satisfy result is TRUE for props and filters: ', featureProperties, filters);
    return result;
  }

  findSubstrings(featureName: string, strFilter: any, value: string | number) {
    if (featureName === 'license_plate_number' && strFilter) {
      const regexPattern = strFilter.map((item: string) => `(?=.*${item.replace('?', '.*')})`).join('') + '.*';
      const regex = new RegExp(convertSimilarCyrillicToLatin(regexPattern.toLocaleUpperCase()));
      return regex.test(String(value));
    } else {
      return String(value).includes(strFilter);
    }
  }
}

export const featuresModule = new FeaturesModule();
